/**
 * Indian states and union territories, with their GST state codes.
 *
 * The code is not decoration. GST decides between CGST+SGST and IGST by
 * comparing the seller's state with the buyer's, and the first two digits of
 * every GSTIN are that code — so an address collected without one cannot be
 * taxed correctly, and an invoice built from it is wrong in a way the customer
 * will not notice and an auditor will.
 *
 * The list is the statutory one, ordered as people expect to find it: states
 * alphabetically, then the union territories. Codes are strings, because `05`
 * is not `5` and dropping the leading zero silently breaks GSTIN matching.
 */

export interface IndianState {
  /** Two-digit GST state code, leading zero preserved. */
  code: string;
  name: string;
  /** Union territory rather than a state. */
  unionTerritory?: boolean;
}

export const INDIAN_STATES: readonly IndianState[] = [
  { code: '37', name: 'Andhra Pradesh' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '18', name: 'Assam' },
  { code: '10', name: 'Bihar' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '30', name: 'Goa' },
  { code: '24', name: 'Gujarat' },
  { code: '06', name: 'Haryana' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '20', name: 'Jharkhand' },
  { code: '29', name: 'Karnataka' },
  { code: '32', name: 'Kerala' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '27', name: 'Maharashtra' },
  { code: '14', name: 'Manipur' },
  { code: '17', name: 'Meghalaya' },
  { code: '15', name: 'Mizoram' },
  { code: '13', name: 'Nagaland' },
  { code: '21', name: 'Odisha' },
  { code: '03', name: 'Punjab' },
  { code: '08', name: 'Rajasthan' },
  { code: '11', name: 'Sikkim' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '36', name: 'Telangana' },
  { code: '16', name: 'Tripura' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '19', name: 'West Bengal' },
  // ── Union territories ──
  { code: '35', name: 'Andaman and Nicobar Islands', unionTerritory: true },
  { code: '04', name: 'Chandigarh', unionTerritory: true },
  { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu', unionTerritory: true },
  { code: '07', name: 'Delhi', unionTerritory: true },
  { code: '01', name: 'Jammu and Kashmir', unionTerritory: true },
  { code: '38', name: 'Ladakh', unionTerritory: true },
  { code: '31', name: 'Lakshadweep', unionTerritory: true },
  { code: '34', name: 'Puducherry', unionTerritory: true },
] as const;

/** State name → GST code, for turning a selected address into something taxable. */
export const STATE_CODE_MAP: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(INDIAN_STATES.map((s) => [s.name, s.code])),
);

/** GST code → state name, for rendering an invoice from the code it stored. */
export const STATE_NAME_BY_CODE: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(INDIAN_STATES.map((s) => [s.code, s.name])),
);

/**
 * Look up a state by name, tolerantly.
 *
 * Addresses arrive from humans, spreadsheets and courier APIs, so the same
 * state turns up as "Tamil Nadu", "TAMIL NADU" and "tamilnadu". Matching
 * strictly would drop the tax code on an address that is perfectly valid.
 */
export function findIndianState(name: string): IndianState | undefined {
  const normalise = (v: string) =>
    v
      .trim()
      .toLowerCase()
      .replace(/[\s.&-]+/g, '');
  const target = normalise(name);
  return INDIAN_STATES.find((s) => normalise(s.name) === target);
}

/** The GST state code for a state name, or undefined if it is not one. */
export function stateCodeFor(name: string): string | undefined {
  return findIndianState(name)?.code;
}
